import { useCallback, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import RestHttpClient from '../../../common/RestHttpClient';
import { ApiException, ProcessWaitingFor, ReturnProcessActionModel } from '../../../generated/ApiClient';
import type { IReturnProcessStateResponse } from '../../../generated/ApiClient';
import { returnProcessRefetchInterval, transitionWindowMs } from './returnProcessPolling';

export const returnProcessQueryKey = (cmsId?: string) =>
  ['dashboard', 'returnProcess', cmsId] as const;

export interface ReturnProcessActionRequest {
  correlationKey: string;
  action: string;
  data: Record<string, string | number | boolean | null>;
}

// The generated client throws on 204; here it just means no process is running.
const isNoContent = (error: unknown) => error instanceof ApiException && error.status === 204;

// 409 means the correlation key was already consumed - reload instead of reporting a failure.
const isAlreadyConsumed = (error: unknown) => error instanceof ApiException && error.status === 409;

export function useReturnProcess(cmsId?: string) {
  const queryClient = useQueryClient();

  const [sentKey, setSentKey] = useState<string>();
  const sentKeyRef = useRef<string>();

  // End of the fast-poll phase after an action. A ref, not state: it is read inside the
  // refetchInterval callback, which react-query re-evaluates on every result and option change
  // anyway — turning it into state would add renders without changing what is read.
  const burstUntilRef = useRef(0);

  const query = useQuery({
    queryKey: returnProcessQueryKey(cmsId),
    queryFn: async (): Promise<IReturnProcessStateResponse | undefined> => {
      try {
        const { data } = await RestHttpClient.getReturnProcess(cmsId!);
        return data;
      } catch (error) {
        if (isNoContent(error)) return undefined;
        throw error;
      }
    },
    enabled: !!cmsId,
    refetchInterval: (data) =>
      returnProcessRefetchInterval({
        status: data?.status,
        // Only decredo's engine produces the next halt on its own; a human or an external
        // system holding the process is a wait of minutes or hours, not of seconds.
        engineIsWorking: data?.waitingFor === ProcessWaitingFor.Engine,
        currentCorrelationKey: data?.current?.correlationKey,
        sentCorrelationKey: sentKeyRef.current,
        burstUntil: burstUntilRef.current,
        now: Date.now(),
      }),
    staleTime: 0,
    retry: false,
  });

  const reload = useCallback(
    () => queryClient.invalidateQueries(returnProcessQueryKey(cmsId)),
    [queryClient, cmsId]
  );

  const actionMutation = useMutation({
    mutationFn: async (request: ReturnProcessActionRequest) => {
      sentKeyRef.current = request.correlationKey;
      // Open the fast-poll phase before the request goes out: decredo acknowledges the signal and
      // finishes the step asynchronously, so the next halt can appear while this POST is still in
      // flight and the reload in onSettled may already race it.
      burstUntilRef.current = Date.now() + transitionWindowMs;
      setSentKey(request.correlationKey);
      const { data } = await RestHttpClient.sendReturnProcessAction(
        cmsId!,
        new ReturnProcessActionModel({
          correlationKey: request.correlationKey,
          action: request.action,
          data: request.data,
        })
      );
      return data;
    },
    onSuccess: (data) => {
      if (data?.current) {
        queryClient.setQueryData(returnProcessQueryKey(cmsId), data);
        sentKeyRef.current = undefined;
        burstUntilRef.current = 0;
        setSentKey(undefined);
      }
    },
    onError: (error) => {
      // A consumed key (409) keeps the fast phase: the step did advance, the reload below will
      // pick it up. Any other failure means nothing is coming, so stop polling fast.
      if (!isAlreadyConsumed(error)) {
        sentKeyRef.current = undefined;
        burstUntilRef.current = 0;
        setSentKey(undefined);
      }
    },
    onSettled: () => {
      reload();
    },
  });

  const state = query.data;
  const hasState = query.isSuccess && !!state;

  const isTransitioning =
    actionMutation.isLoading || (!!sentKey && state?.current?.correlationKey === sentKey);

  return {
    state,
    documents: state?.documents ?? [],
    hasProcess: hasState,
    isInitialLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    actionError: actionMutation.isError && !isAlreadyConsumed(actionMutation.error),
    dismissActionError: actionMutation.reset,
    refetch: reload,
    sendAction: actionMutation.mutate,
    isTransitioning,
  };
}
